import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Heart, Sparkles, ChevronRight } from "lucide-react";
import RecipeCard from "@/components/RecipeCard";
import EmailSignup from "@/components/EmailSignup";
import { fetchFeatured, fetchNew } from "@/lib/api";

const HERO_IMG =
  "https://images.pexels.com/photos/35285814/pexels-photo-35285814.jpeg?auto=compress&cs=tinysrgb&w=1600";

const REASONS = [
  {
    icon: Zap,
    title: "Faster",
    body: "Most recipes go from raw to plated in 20 minutes or less — no preheating an entire oven.",
  },
  {
    icon: Heart,
    title: "Healthier",
    body: "Up to 80% less oil than deep frying, with all the crispy crunch you actually want.",
  },
  {
    icon: Sparkles,
    title: "Easier",
    body: "Set the temp, set a timer, walk away. Cleanup is a single basket and 30 seconds.",
  },
];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [newRecipes, setNewRecipes] = useState([]);

  useEffect(() => {
    document.title = "JustAirFry — Simply Crispy. Simply Delicious.";
    fetchFeatured().then(setFeatured).catch(() => setFeatured([]));
    fetchNew().then(setNewRecipes).catch(() => setNewRecipes([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundColor: "var(--jaf-bg)" }}
      >
        <div className="jaf-container py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span
              className="inline-block text-xs tracking-[0.18em] uppercase font-semibold mb-5"
              style={{ color: "var(--jaf-primary)" }}
            >
              The Air Fryer Kitchen
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Simply Crispy. <br />
              <span style={{ color: "var(--jaf-primary)" }}>Simply Delicious.</span>
            </h1>
            <p
              className="mt-6 text-base sm:text-lg max-w-xl leading-relaxed"
              style={{ color: "var(--jaf-text-muted)" }}
            >
              Cozy, family-tested recipes that make the most of your air fryer.
              Wholesome ingredients, real results, ready in minutes.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/recipes"
                data-testid="hero-browse-recipes"
                className="jaf-btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium"
              >
                Browse Recipes <ArrowRight size={16} />
              </Link>
              <Link
                to="/about"
                data-testid="hero-meet-us"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium border"
                style={{
                  borderColor: "var(--jaf-border)",
                  color: "var(--jaf-text)",
                  backgroundColor: "#fff",
                }}
              >
                Meet the kitchen
              </Link>
            </div>
          </div>

          <div className="relative">
            <div
              className="absolute -inset-6 rounded-[2rem] -z-10"
              style={{ backgroundColor: "var(--jaf-surface)" }}
            />
            <img
              src={HERO_IMG}
              alt="Golden crispy air fryer chicken pieces"
              className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              data-testid="hero-image"
            />
          </div>
        </div>
      </section>

      {/* WHY YOU'LL LOVE */}
      <section
        className="py-16 md:py-20"
        style={{ backgroundColor: "var(--jaf-surface)" }}
        data-testid="why-section"
      >
        <div className="jaf-container">
          <div className="max-w-2xl">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Why you'll love air frying
            </h2>
            <p
              className="mt-4 text-base"
              style={{ color: "var(--jaf-text-muted)" }}
            >
              Three good reasons your countertop deserves an air fryer.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {REASONS.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                data-testid={`reason-${title.toLowerCase()}`}
                className="bg-white rounded-xl p-7"
                style={{ border: "1px solid var(--jaf-border)" }}
              >
                <span
                  className="inline-flex h-12 w-12 items-center justify-center rounded-full mb-5"
                  style={{
                    backgroundColor: "rgba(200, 90, 50, 0.1)",
                    color: "var(--jaf-primary)",
                  }}
                >
                  <Icon size={22} />
                </span>
                <h3 className="font-serif text-2xl mb-2">{title}</h3>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: "var(--jaf-text-muted)" }}
                >
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED RECIPES */}
      <section className="py-16 md:py-20" data-testid="featured-section">
        <div className="jaf-container">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div className="max-w-xl">
              <span
                className="inline-block text-xs tracking-[0.18em] uppercase font-semibold mb-3"
                style={{ color: "var(--jaf-secondary)" }}
              >
                Reader favourites
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                Recipes everyone is making
              </h2>
            </div>
            <Link
              to="/recipes"
              data-testid="featured-view-all"
              className="text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all"
              style={{ color: "var(--jaf-primary)" }}
            >
              View all recipes <ChevronRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {featured.slice(0, 6).map((r) => (
              <RecipeCard key={r.id} recipe={r} testIdPrefix="featured-card" />
            ))}
          </div>
        </div>
      </section>

      {/* NEW THIS WEEK — horizontal scroll */}
      <section
        className="py-16 md:py-20"
        style={{ backgroundColor: "var(--jaf-surface)" }}
        data-testid="new-section"
      >
        <div className="jaf-container">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <span
                className="inline-block text-xs tracking-[0.18em] uppercase font-semibold mb-3"
                style={{ color: "var(--jaf-primary)" }}
              >
                Fresh from the kitchen
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">
                New this week
              </h2>
            </div>
          </div>

          <div
            className="horizontal-scroll flex gap-5 overflow-x-auto pb-4 -mx-4 px-4"
            data-testid="new-recipes-scroll"
          >
            {newRecipes.map((r) => (
              <div key={r.id} className="min-w-[280px] max-w-[300px] flex-shrink-0">
                <RecipeCard recipe={r} testIdPrefix="new-card" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EMAIL SIGNUP */}
      <section className="py-16 md:py-20" data-testid="email-section">
        <div className="jaf-container">
          <EmailSignup source="homepage" testIdPrefix="home-signup" />
        </div>
      </section>
    </div>
  );
}
